import { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import { useAuth } from '@/contexts/AuthContext';
import { biometricService, staffService, AttendanceRecord, Staff } from '@/lib/storage';
import { areModelsLoaded, loadModels } from '@/services/faceService';
import { getCurrentLocation } from '@/services/locationService';
import { useVoiceSettings } from '@/hooks/useVoiceSettings';
import ExpenseModal from '@/components/ExpenseModal';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Camera,
  MapPin,
  Scan,
  LogIn,
  LogOut,
  Loader2,
  AlertTriangle,
  Wallet,
  Bike,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type ModalStep =
  | 'idle'
  | 'scanning'
  | 'locating'
  | 'submitting'
  | 'success'
  | 'face_failed'
  | 'location_failed'
  | 'error';

interface SelfAttendanceModalProps {
  open: boolean;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(isoString: string | undefined): string {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function TodayStatusBadge({ record }: { record: AttendanceRecord | null }) {
  if (!record) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Clock className="w-4 h-4" />
        <span>Not checked in today</span>
      </div>
    );
  }

  const getVerificationBadge = () => {
    if (record.verification === 'pending') {
      return <span className="text-[10px] bg-amber-100 text-amber-750 dark:bg-amber-950/30 dark:text-amber-300 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">Pending Location Approval</span>;
    }
    if (record.verification === 'rejected') {
      return <span className="text-[10px] bg-rose-100 text-rose-750 dark:bg-rose-950/30 dark:text-rose-350 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">Outside Location Rejected</span>;
    }
    if (record.verification === 'approved') {
      return <span className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-350 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">Outside Location Approved</span>;
    }
    return null;
  };

  if (record.checkIn && !record.checkOut) {
    return (
      <div className="flex flex-col gap-1.5 items-start">
        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="w-4 h-4" />
          <span>Checked in at <strong>{formatTime(record.checkIn)}</strong></span>
          {record.status === 'late' && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full">Late</span>
          )}
        </div>
        {getVerificationBadge()}
      </div>
    );
  }
  if (record.checkIn && record.checkOut) {
    return (
      <div className="flex flex-col gap-1.5 items-start">
        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
          <LogIn className="w-4 h-4" />
          <span>In: <strong>{formatTime(record.checkIn)}</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <LogOut className="w-4 h-4" />
          <span>Out: <strong>{formatTime(record.checkOut)}</strong></span>
          {record.workingHours !== undefined && record.workingHours > 0 && (
            <span className="text-xs text-muted-foreground">({record.workingHours.toFixed(1)}h)</span>
          )}
        </div>
        {getVerificationBadge()}
      </div>
    );
  }
  return null;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SelfAttendanceModal({ open, onClose }: SelfAttendanceModalProps) {
  const { session } = useAuth();
  const { playSound } = useVoiceSettings();

  const [step, setStep] = useState<ModalStep>('idle');
  const [todayRecord, setTodayRecord] = useState<AttendanceRecord | null | undefined>(undefined);
  const [statusMsg, setStatusMsg] = useState('');
  const [result, setResult] = useState<{ type: 'checkin' | 'checkout'; record: AttendanceRecord; message: string } | null>(null);
  const [showExpense, setShowExpense] = useState(false);
  const [staffProfile, setStaffProfile] = useState<Staff | null>(null);
  const [kmInput, setKmInput] = useState('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const isCheckedIn = !!(todayRecord?.checkIn && !todayRecord?.checkOut);

  useEffect(() => {
    if (step === 'success') {
      playSound('success');
    } else if (step === 'face_failed') {
      playSound('fail');
    }
  }, [step, playSound]);

  const webcamRef = useRef<Webcam>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Fetch today's status each time the modal opens
  useEffect(() => {
    if (!open || !session?.userId) return;
    setStep('idle');
    setResult(null);
    setStatusMsg('');
    setTodayRecord(undefined);
    setStaffProfile(null);
    setKmInput('');

    biometricService.getTodayAttendance(session.userId)
      .then(rec => setTodayRecord(rec))
      .catch(() => setTodayRecord(null));

    staffService.getById(session.userId)
      .then(profile => setStaffProfile(profile))
      .catch(() => setStaffProfile(null));
  }, [open, session?.userId]);

  // Camera cleanup helper
  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) stopCamera();
    return () => stopCamera();
  }, [open, stopCamera]);

  // Face scanning loop
  const startScanning = useCallback(async () => {
    if (!session) return;

    if (!areModelsLoaded()) {
      setIsLoadingModels(true);
      setStatusMsg('Loading face recognition models... Please wait.');
      const success = await loadModels();
      setIsLoadingModels(false);
      if (!success) {
        setStep('error');
        setStatusMsg('Failed to load face recognition models. Please check your internet connection and try again.');
        return;
      }
      setStatusMsg('');
    }

    setStep('scanning');
    setStatusMsg('');

    // Fetch only this staff member's own faceDescriptor
    let ownDescriptor: number[];
    try {
      const staffData = await staffService.getById(session.userId);
      if (!staffData?.faceDescriptor || staffData.faceDescriptor.length !== 128) {
        setStep('error');
        setStatusMsg('No face registered on your account. Please ask an admin to register your face first.');
        return;
      }
      ownDescriptor = staffData.faceDescriptor;
    } catch {
      setStep('error');
      setStatusMsg('Could not fetch your face data. Please check your connection and try again.');
      return;
    }

    // Poll every 800 ms for a face (ensuring no concurrent/overlapping scans)
    let isProcessing = false;
    scanIntervalRef.current = setInterval(async () => {
      if (isProcessing) return;
      if (!scanIntervalRef.current) return;

      const video = webcamRef.current?.video;
      if (!video || video.readyState !== 4) return;

      isProcessing = true;
      try {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) {
          isProcessing = false;
          return;
        }

        if (scanIntervalRef.current === null) {
          isProcessing = false;
          return;
        }

        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;

        // Client-side Euclidean distance — same 1.5 threshold as the kiosk page
        const live = detection.descriptor;
        const stored = new Float32Array(ownDescriptor);
        let sum = 0;
        for (let i = 0; i < 128; i++) {
          const diff = live[i] - stored[i];
          sum += diff * diff;
        }
        const dist = Math.sqrt(sum);
        console.log(`🔍 Self-attendance face distance: ${dist.toFixed(3)}`);

        if (dist >= 0.6) {
          stopCamera();
          setStep('face_failed');
          setStatusMsg('TU KAUN HAI BE');
          return;
        }

        // Face matched — now get GPS location
        setStep('locating');
        stopCamera();

        let latitude: number, longitude: number;
        try {
          const loc = await getCurrentLocation();
          latitude = loc.latitude;
          longitude = loc.longitude;
        } catch (locErr: any) {
          setStep('location_failed');
          setStatusMsg(locErr?.message || 'Could not get your location. Please enable location access and try again.');
          return;
        }

        // Submit to backend (which re-verifies face + location server-side)
        setStep('submitting');
        try {
          const travelsByBike = !!staffProfile?.travelsByBike;
          const scanResult = await biometricService.selfScan({
            staffId: session.userId,
            staffName: session.name,
            descriptor: live,
            latitude,
            longitude,
            kmTraveled: travelsByBike && isCheckedIn ? (parseFloat(kmInput) || 0) : undefined,
          });
          setResult(scanResult);
          setTodayRecord(scanResult.record);
          setStep('success');
        } catch (err: any) {
          // The api helper throws on non-ok responses — try to get the JSON body
          let errStep: ModalStep = 'error';
          let errMsg = 'Something went wrong. Please try again.';
          try {
            const body = JSON.parse(err?.message?.replace('API Error: ', '') || '{}');
            if (body?.reason === 'face_mismatch') {
              errStep = 'face_failed';
              errMsg = 'TU KAUN HAI BE';
            } else if (body?.reason === 'location_denied') {
              errStep = 'location_failed';
              errMsg = body.error || 'You are outside the office area.';
            } else if (body?.error) {
              errMsg = body.error;
            }
          } catch { /* ignore parse errors */ }
          // Fetch the actual body from the error response if available
          if (err?.response) {
            try {
              const body = await err.response.json();
              if (body?.reason === 'face_mismatch') { errStep = 'face_failed'; errMsg = 'TU KAUN HAI BE'; }
              else if (body?.reason === 'location_denied') { errStep = 'location_failed'; errMsg = body.error || errMsg; }
              else if (body?.error) { errMsg = body.error; }
            } catch { /* ignore */ }
          }
          setStep(errStep);
          setStatusMsg(errMsg);
        }
      } catch (detectionErr) {
        console.error('Face detection error:', detectionErr);
      }
    }, 800);
  }, [session, stopCamera, staffProfile, isCheckedIn, kmInput]);

  const handleRetry = () => {
    setStatusMsg('');
    startScanning();
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const actionLabel = isCheckedIn ? 'Check Out' : 'Check In';
  const ActionIcon = isCheckedIn ? LogOut : LogIn;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="w-full h-full sm:max-w-md sm:h-auto sm:rounded-3xl p-0 overflow-hidden flex flex-col bg-white dark:bg-slate-900 border-none shadow-2xl transition-all">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-850 dark:text-slate-100">
            <Scan className="w-5 h-5 text-indigo-500 animate-pulse" />
            Biometric Scan
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400 font-semibold mt-0.5">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>

        {/* Content Body */}
        <div className="px-6 py-6 flex-1 flex flex-col justify-between overflow-y-auto space-y-6">

          {/* Today's status strip */}
          <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-850 rounded-2xl px-4 py-3 min-h-[52px] shrink-0">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Today's Register</span>
            {todayRecord === undefined ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <TodayStatusBadge record={todayRecord} />
            )}
          </div>

          {/* IDLE STEP */}
          {step === 'idle' && (
            <div className="flex-1 flex flex-col justify-center space-y-6 my-auto">
              <div className="flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-24 h-24 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-500 animate-pulse">
                  <Scan className="w-12 h-12" />
                </div>
                <h3 className="font-bold text-base text-slate-800 dark:text-slate-100">Ready to Scan Face</h3>
                <p className="text-xs text-slate-400 max-w-[280px] leading-relaxed font-semibold">
                  Make sure you are in a well-lit area and hold your phone directly in front of your face.
                </p>
              </div>

              {staffProfile?.travelsByBike && isCheckedIn && (
                <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800/40 animate-in fade-in slide-in-from-bottom-2 duration-200">
                  <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                    <Bike className="w-3.5 h-3.5 text-indigo-500" />
                    Kilometers Traveled Today
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter total KM traveled today"
                    value={kmInput}
                    onChange={(e) => setKmInput(e.target.value)}
                    className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 font-bold"
                  />
                  {parseFloat(kmInput) > 0 && (
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold leading-none mt-1 animate-in fade-in duration-200">
                      Calculated travel allowance: ₹{((parseFloat(kmInput) || 0) * (staffProfile?.bikeRatePerKm || 0)).toFixed(2)} (₹{staffProfile?.bikeRatePerKm}/km)
                    </p>
                  )}
                </div>
              )}

              <Button
                variant="royal"
                className="w-full h-11.5 rounded-xl font-bold gap-2 text-sm shadow-lg shadow-primary/5"
                onClick={startScanning}
                disabled={todayRecord === undefined || isLoadingModels}
              >
                {isLoadingModels ? (
                  <>
                    <Loader2 className="w-4.5 h-4.5 animate-spin" />
                    Loading Models...
                  </>
                ) : (
                  <>
                    <ActionIcon className="w-4.5 h-4.5" />
                    Start {actionLabel}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* SCANNING STEP */}
          {step === 'scanning' && (
            <div className="flex-1 flex flex-col justify-between space-y-6">
              <div className="relative rounded-3xl overflow-hidden bg-black aspect-square w-full max-w-[280px] mx-auto shadow-2xl border-4 border-slate-100 dark:border-slate-800">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ width: 360, height: 360, facingMode: 'user' }}
                  className="w-full h-full object-cover"
                  onUserMedia={(stream) => { streamRef.current = stream; }}
                />
                {/* Immersive Face Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none bg-black/10">
                  <div className="border-4 border-dashed border-indigo-500/60 rounded-full w-48 h-48 animate-spin" style={{ animationDuration: '20s' }} />
                  <div className="absolute border-2 border-emerald-500/40 rounded-full w-40 h-40 animate-pulse" />
                  <div className="absolute inset-0 border-[24px] border-black/40" />
                  <p className="absolute bottom-6 text-[10px] text-white/90 bg-indigo-600/90 font-bold tracking-wider px-3.5 py-1 rounded-full uppercase shadow-md">
                    Scanning Face…
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl font-semibold"
                onClick={() => { stopCamera(); setStep('idle'); }}
              >
                Cancel Scan
              </Button>
            </div>
          )}

          {/* LOCATING STEP */}
          {step === 'locating' && (
            <div className="flex-1 flex flex-col justify-center items-center gap-4 my-auto">
              <div className="w-20 h-20 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                <MapPin className="w-10 h-10 text-indigo-500 animate-bounce" />
              </div>
              <div className="text-center space-y-1">
                <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">Face Verified Successfully</h3>
                <p className="text-xs text-slate-400 font-semibold">Retrieving secure GPS coordinates…</p>
              </div>
            </div>
          )}

          {/* SUBMITTING STEP */}
          {step === 'submitting' && (
            <div className="flex-1 flex flex-col justify-center items-center gap-4 my-auto">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-indigo-100 dark:border-indigo-950/30" />
                <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-indigo-550 border-t-transparent animate-spin" />
              </div>
              <div className="text-center space-y-1">
                <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">Uploading Attendance</h3>
                <p className="text-xs text-slate-400 font-semibold">Securing transaction parameters…</p>
              </div>
            </div>
          )}

          {/* SUCCESS STEP */}
          {step === 'success' && result && (
            <div className="flex-1 flex flex-col justify-between space-y-6">
              <div className="flex flex-col items-center justify-center text-center space-y-4 my-auto">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${
                  result.type === 'checkin'
                    ? 'bg-emerald-500/10 text-emerald-555 border border-emerald-500/20 shadow-emerald-500/5'
                    : 'bg-indigo-500/10 text-indigo-555 border border-indigo-500/20 shadow-indigo-500/5'
                }`}>
                  {result.type === 'checkin'
                    ? <LogIn className="w-10 h-10" />
                    : <LogOut className="w-10 h-10" />}
                </div>
                <div className="space-y-1.5 px-4">
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">{result.message}</h3>
                  <p className="text-xs text-slate-400 font-semibold leading-relaxed">
                    Time recorded:{' '}
                    <strong className="text-slate-700 dark:text-slate-200">
                      {result.type === 'checkin'
                        ? formatTime(result.record.checkIn)
                        : formatTime(result.record.checkOut)}
                    </strong>
                    {result.record.workingHours && result.record.workingHours > 0
                      ? ` (${result.record.workingHours.toFixed(1)} hrs total shift)`
                      : ''}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                {result.type === 'checkout' && (
                  <Button
                    variant="outline"
                    className="w-full h-11 rounded-xl font-bold gap-2 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    onClick={() => { setShowExpense(true); }}
                  >
                    <Wallet className="w-4 h-4" />
                    Add Expense / Claims
                  </Button>
                )}
                <Button 
                  onClick={handleClose}
                  className="w-full h-11 rounded-xl font-bold bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-50 shadow-md"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
          <ExpenseModal open={showExpense} onClose={() => setShowExpense(false)} />

          {/* FACE FAILED */}
          {step === 'face_failed' && (
            <div className="flex-1 flex flex-col justify-between space-y-6">
              <div className="flex flex-col items-center justify-center text-center space-y-4 my-auto">
                <div className="w-20 h-20 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500">
                  <XCircle className="w-10 h-10" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-rose-600 dark:text-rose-450 text-sm">Face Not Recognized</h3>
                  <p className="text-xs text-slate-400 font-semibold leading-relaxed max-w-xs">{statusMsg}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="w-full h-11 rounded-xl font-semibold" onClick={handleClose}>Cancel</Button>
                <Button variant="royal" className="w-full h-11 rounded-xl font-bold gap-1.5 shadow-md shadow-primary/5" onClick={handleRetry}>
                  <Camera className="w-4 h-4" /> Retry
                </Button>
              </div>
            </div>
          )}

          {/* LOCATION FAILED */}
          {step === 'location_failed' && (
            <div className="flex-1 flex flex-col justify-between space-y-6">
              <div className="flex flex-col items-center justify-center text-center space-y-4 my-auto">
                <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
                  <MapPin className="w-10 h-10 animate-bounce" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-amber-600 dark:text-amber-450 text-sm">Outside Office Geofence</h3>
                  <p className="text-xs text-slate-400 font-semibold leading-relaxed max-w-xs">{statusMsg}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="w-full h-11 rounded-xl font-semibold" onClick={handleClose}>Cancel</Button>
                <Button variant="royal" className="w-full h-11 rounded-xl font-bold gap-1.5 shadow-md shadow-primary/5" onClick={handleRetry}>
                  <MapPin className="w-4 h-4" /> Retry
                </Button>
              </div>
            </div>
          )}

          {/* GENERIC ERROR */}
          {step === 'error' && (
            <div className="flex-1 flex flex-col justify-between space-y-6">
              <div className="flex flex-col items-center justify-center text-center space-y-4 my-auto">
                <div className="w-20 h-20 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500">
                  <AlertTriangle className="w-10 h-10" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">Something went wrong</h3>
                  <p className="text-xs text-slate-400 font-semibold leading-relaxed max-w-xs">{statusMsg}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full h-11 rounded-xl font-semibold" onClick={handleClose}>Close</Button>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
